import React, { useCallback } from "react";
import { useT, Namespace } from "@racoon/i18n";
import { Button, useModal, useToast } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { useDeleteTeamMutation } from "~/shared/api/team/team.grpc";
import { TeamForm } from "./team-form";

type Team = ConnectProto.Team;

/**
 * Opens the create / edit team modal, with its delete confirmation. Shared by the
 * teams settings tab and the shift schedule's team headings.
 */
export const useTeamModal = (selectedCompanyId?: number) => {
  const t = useT();
  const { toast } = useToast();
  const { openModal, closeModal } = useModal();
  const [deleteTeam] = useDeleteTeamMutation();

  const confirmDelete = useCallback(
    (team: Team) => {
      const modalId = openModal({
        title: t(Namespace.Settings, "teams.dialog.deleteConfirmTitle"),
        closeButtonTop: "top-4",
        size: "sm",
        body: (
          <p className="text-center text-sm text-text-primary">
            {t(Namespace.Settings, "teams.dialog.deleteConfirmDescription", { name: team.name })}
          </p>
        ),
        footer: (
          <div className="flex w-full justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => closeModal(modalId)}>
              {t(Namespace.Buttons, "cancel")}
            </Button>
            <Button
              size="sm"
              variant="opacityRedWithOutline"
              onClick={async () => {
                closeModal(modalId);
                try {
                  await deleteTeam({ id: team.id }).unwrap();
                  toast({ title: t(Namespace.Settings, "teams.dialog.deleteSuccess") });
                } catch {
                  // GlobalErrorToaster shows the API error.
                }
              }}
            >
              {t(Namespace.Buttons, "delete")}
            </Button>
          </div>
        ),
      });
    },
    [t, openModal, closeModal, deleteTeam, toast],
  );

  const openTeamModal = useCallback(
    (team: Team | null) => {
      if (!selectedCompanyId) return;
      const modalId = openModal({
        title: t(Namespace.Settings, team ? "teams.dialog.editTitle" : "teams.dialog.createTitle"),
        width: "36rem",
        bodyClassName: "!p-0",
        body: (
          <TeamForm
            team={team}
            ownerOrganizationId={selectedCompanyId}
            onClose={() => closeModal(modalId)}
            onDelete={(target) => {
              closeModal(modalId);
              confirmDelete(target);
            }}
          />
        ),
      });
    },
    [selectedCompanyId, t, openModal, closeModal, confirmDelete],
  );

  return openTeamModal;
};
